import { ContentState } from 'draft-js'
import { stateFromMarkdown } from 'draft-js-import-markdown'

/**
 * Collapses runs of spaces/tabs *between* words on a line to a single space.
 *
 * Runs at the start of a line (list indentation) and at the end of a line
 * (two trailing spaces are a markdown hard break) are left untouched.
 *
 * The editor already collapses these runs when it renders the note, so this
 * changes nothing the user can see. It exists to work around a crash in
 * draft-js-import-element (pulled in by draft-js-import-markdown), whose
 * `collapseWhiteSpace` removes collapsed characters one at a time with
 *
 *   characterMeta = characterMeta.slice(0, i).concat(characterMeta.slice(i + 1))
 *
 * on a *lazy* Immutable `Seq`. Every removed character wraps the Seq in one
 * more slice/concat layer and resolving it at the end recurses once per layer,
 * so a single paragraph with roughly 600+ collapsible whitespace characters
 * throws "RangeError: Maximum call stack size exceeded" and unmounts the whole
 * record page. Questionnaire results pasted from Embloom look exactly like
 * that: every question/answer pair is separated by "    \t\t" plus forty
 * spaces, all on one line.
 */
export const collapseInlineWhitespace = (markdown: string): string =>
  markdown.replace(/(\S)[ \t]{2,}(?=\S)/g, '$1 ')

/**
 * `stateFromMarkdown` that never takes the page down. Normalises the
 * pathological whitespace first and, should parsing still blow the stack
 * (whitespace in a shape the regex above leaves alone), falls back to showing
 * the raw markdown as plain text rather than rendering nothing.
 */
export const safeStateFromMarkdown = (markdown: string): ContentState => {
  const normalized = collapseInlineWhitespace(markdown)
  try {
    return stateFromMarkdown(normalized)
  } catch (e) {
    console.error('stateFromMarkdown failed, showing the note as plain text', e)
    return ContentState.createFromText(normalized)
  }
}
